import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { fetchBiUiVisibility, saveBiUiVisibility } from '../api/uiVisibility'
import { useBiAuth } from './BiAuthContext'

export const UI_VISIBILITY_STORAGE_KEY = 'fiesta-bi:ui-visibility-v1'

/** Itens do menu lateral que podem ser ocultados (permanecem no código/rotas). */
export const TOGGLEABLE_NAV = [
  { id: 'margens', label: 'Margens', path: '/margens' },
  { id: 'operacoes', label: 'Operações', path: '/operacoes' },
  { id: 'pedidos', label: 'Pedidos', path: '/pedidos' },
  { id: 'fiscal', label: 'Fiscal', path: '/fiscal' },
  { id: 'agenda', label: 'Agenda', path: '/agenda' },
  { id: 'pagamentos', label: 'Pagamentos', path: '/pagamentos' },
  { id: 'fretes', label: 'Fretes', path: '/fretes' },
  { id: 'despesas', label: 'Despesas', path: '/despesas' },
  { id: 'clientes', label: 'Clientes', path: '/clientes' },
] as const

export type ToggleableNavId = (typeof TOGGLEABLE_NAV)[number]['id']

export type BiUiVisibility = {
  fonteDeDados: boolean
  proveniencia: boolean
  nav: Record<ToggleableNavId, boolean>
}

/** Visível só o que está na ordem principal; o resto via Ctrl+H. */
export const DEFAULT_UI_VISIBILITY: BiUiVisibility = {
  fonteDeDados: true,
  proveniencia: true,
  nav: {
    margens: false,
    operacoes: false,
    pedidos: false,
    fiscal: false,
    agenda: false,
    pagamentos: false,
    fretes: false,
    despesas: false,
    clientes: false,
  },
}

function isToggleableNavId(id: string): id is ToggleableNavId {
  return TOGGLEABLE_NAV.some((item) => item.id === id)
}

/** Lê legado localStorage (migração única quando o servidor ainda não tem prefs). */
export function loadUiVisibilityLocal(): BiUiVisibility {
  try {
    const raw = localStorage.getItem(UI_VISIBILITY_STORAGE_KEY)
    if (!raw) return { ...DEFAULT_UI_VISIBILITY, nav: { ...DEFAULT_UI_VISIBILITY.nav } }
    const parsed = JSON.parse(raw) as Partial<BiUiVisibility>
    const nav = { ...DEFAULT_UI_VISIBILITY.nav }
    if (parsed.nav && typeof parsed.nav === 'object') {
      for (const [key, value] of Object.entries(parsed.nav)) {
        if (isToggleableNavId(key) && typeof value === 'boolean') {
          nav[key] = value
        }
      }
    }
    return {
      fonteDeDados:
        typeof parsed.fonteDeDados === 'boolean'
          ? parsed.fonteDeDados
          : DEFAULT_UI_VISIBILITY.fonteDeDados,
      proveniencia:
        typeof parsed.proveniencia === 'boolean'
          ? parsed.proveniencia
          : DEFAULT_UI_VISIBILITY.proveniencia,
      nav,
    }
  } catch {
    return { ...DEFAULT_UI_VISIBILITY, nav: { ...DEFAULT_UI_VISIBILITY.nav } }
  }
}

/** Pathname oculto pelas prefs? (ex.: /fiscal, /clientes/…). */
export function isNavPathHidden(pathname: string, prefs: BiUiVisibility): boolean {
  return TOGGLEABLE_NAV.some(
    (item) =>
      !prefs.nav[item.id] &&
      (pathname === item.path || pathname.startsWith(`${item.path}/`)),
  )
}

type BiUiVisibilityContextValue = {
  prefs: BiUiVisibility
  canEdit: boolean
  loading: boolean
  saveError: string | null
  setPrefs: (next: BiUiVisibility) => Promise<void>
  dialogOpen: boolean
  openDialog: () => void
  closeDialog: () => void
}

const BiUiVisibilityContext = createContext<BiUiVisibilityContextValue | null>(null)

export function BiUiVisibilityProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useBiAuth()
  const [prefs, setPrefsState] = useState<BiUiVisibility>(() => ({
    ...DEFAULT_UI_VISIBILITY,
    nav: { ...DEFAULT_UI_VISIBILITY.nav },
  }))
  const [canEdit, setCanEdit] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false)
      return
    }
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const data = await fetchBiUiVisibility()
        if (cancelled) return
        setCanEdit(data.canEdit)
        if (data.source === 'mongo') {
          setPrefsState(data.prefs)
        } else if (data.canEdit) {
          const local = loadUiVisibilityLocal()
          const differs =
            local.fonteDeDados !== DEFAULT_UI_VISIBILITY.fonteDeDados ||
            local.proveniencia !== DEFAULT_UI_VISIBILITY.proveniencia ||
            TOGGLEABLE_NAV.some((item) => local.nav[item.id] !== DEFAULT_UI_VISIBILITY.nav[item.id])
          if (differs) {
            try {
              const saved = await saveBiUiVisibility(local)
              if (cancelled) return
              setPrefsState(saved.prefs)
              setCanEdit(saved.canEdit)
              localStorage.removeItem(UI_VISIBILITY_STORAGE_KEY)
            } catch {
              if (!cancelled) setPrefsState(local)
            }
          } else {
            setPrefsState(data.prefs)
          }
        } else {
          setPrefsState(data.prefs)
        }
      } catch {
        if (!cancelled) {
          setPrefsState(loadUiVisibilityLocal())
          setCanEdit(false)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isAuthenticated, user?.email])

  const setPrefs = useCallback(async (next: BiUiVisibility) => {
    setSaveError(null)
    const snapshot = { ...next, nav: { ...next.nav } }
    setPrefsState(snapshot)
    try {
      const saved = await saveBiUiVisibility(snapshot)
      setPrefsState(saved.prefs)
      setCanEdit(saved.canEdit)
      localStorage.removeItem(UI_VISIBILITY_STORAGE_KEY)
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Falha ao guardar'
      setSaveError(msg)
      throw e
    }
  }, [])

  const openDialog = useCallback(() => {
    if (!canEdit) return
    setDialogOpen(true)
  }, [canEdit])
  const closeDialog = useCallback(() => setDialogOpen(false), [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey) return
      if (e.key.toLowerCase() !== 'h') return
      if (!canEdit) return
      const target = e.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return
      e.preventDefault()
      e.stopPropagation()
      setDialogOpen(true)
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [canEdit])

  const value = useMemo(
    () => ({
      prefs,
      canEdit,
      loading,
      saveError,
      setPrefs,
      dialogOpen,
      openDialog,
      closeDialog,
    }),
    [prefs, canEdit, loading, saveError, setPrefs, dialogOpen, openDialog, closeDialog],
  )

  return (
    <BiUiVisibilityContext.Provider value={value}>{children}</BiUiVisibilityContext.Provider>
  )
}

export function useBiUiVisibility() {
  const ctx = useContext(BiUiVisibilityContext)
  if (!ctx) throw new Error('useBiUiVisibility fora de BiUiVisibilityProvider')
  return ctx
}
